import type {
  BookingPassenger,
  InventoryBooking,
  PassengerDetails,
  PaxFareSnapshot,
} from '../api/inventory'
import { parseIsoDate, parseMoney } from './flightFormat'

const CSV_COLUMNS = [
  'booking_id',
  'itinerary_id',
  'plating_carrier',
  'fare_type',
  'last_ticketable_date',
  'booking_created_at',
  'booking_base_amount',
  'booking_tax_amount',
  'booking_total_amount',
  'booking_currency',
  'passenger_prefix',
  'passenger_first_name',
  'passenger_middle_name',
  'passenger_last_name',
  'passenger_gender',
  'passenger_date_of_birth',
  'passenger_type',
  'passenger_status',
  'airline_record_locator',
  'ticket_number',
  'email',
  'phone',
  'fare_pax_code',
  'fare_pax_type',
  'fare_quantity',
  'payment_type',
  'payment_amount',
  'payment_currency',
  'payment_card',
] as const

type CsvColumn = (typeof CSV_COLUMNS)[number]
type CsvRow = Record<CsvColumn, string>

function csvDate(value: string | null | undefined): string {
  const date = parseIsoDate(value)
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function moneyAmount(value: unknown): string {
  const money = parseMoney(value)
  return money ? money.amount.toFixed(2) : ''
}

function moneyCurrency(value: unknown, fallback = ''): string {
  return parseMoney(value)?.currency || fallback
}

function contactValues(
  items: Array<string | { email?: string; number?: string }> | undefined,
  key: 'email' | 'number',
): string {
  return (items || [])
    .map((item) => (typeof item === 'string' ? item : item[key] || ''))
    .filter(Boolean)
    .join('; ')
}

function passengerType(details: PassengerDetails | null | undefined): string {
  return details?.paxType || details?.passengerType || details?.passengerTypeCode || ''
}

function cardLastFour(value: string | null | undefined): string {
  const digits = String(value || '').replace(/\D/g, '')
  return digits ? digits.slice(-4) : ''
}

function fareTotals(fares: PaxFareSnapshot[] | null | undefined) {
  const rows = fares || []
  const base = rows.reduce((sum, fare) => sum + Number(fare.baseAmount?.amount || 0), 0)
  const taxes = rows.reduce((sum, fare) => sum + Number(fare.totalTaxes?.amount || 0), 0)
  const total = rows.reduce((sum, fare) => sum + Number(fare.totalAmount?.amount || 0), 0)
  const currency =
    rows[0]?.totalAmount?.currency ||
    rows[0]?.baseAmount?.currency ||
    rows[0]?.totalTaxes?.currency ||
    'CAD'
  return {
    base: rows.length ? base.toFixed(2) : '',
    taxes: rows.length ? taxes.toFixed(2) : '',
    total: rows.length ? total.toFixed(2) : '',
    currency: rows.length ? currency : '',
  }
}

function fareForPassenger(
  booking: InventoryBooking,
  details: PassengerDetails | null | undefined,
): PaxFareSnapshot | undefined {
  const fares = booking.pax_fare || []
  const code = (details?.passengerTypeCode || '').toUpperCase()
  const type = (details?.paxType || details?.passengerType || '').toLowerCase()
  return (
    fares.find((fare) => {
      const fareCode = (fare.passengerDetails?.code || fare.passengerTypeCode || '').toUpperCase()
      const fareType = (fare.passengerDetails?.type || fare.passengerType || '').toLowerCase()
      return (code && fareCode === code) || (type && fareType === type)
    }) || fares[0]
  )
}

function escapeCsv(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function bookingBaseRow(booking: InventoryBooking): CsvRow {
  const totals = fareTotals(booking.pax_fare)
  const empty = Object.fromEntries(CSV_COLUMNS.map((column) => [column, ''])) as CsvRow
  return {
    ...empty,
    booking_id: booking.booking_id,
    itinerary_id: String(booking.itinerary_id),
    plating_carrier: booking.itinerary?.plating_carrier || '',
    fare_type: booking.itinerary?.fare_type || '',
    last_ticketable_date: csvDate(booking.itinerary?.last_ticketable_date),
    booking_created_at: csvDate(booking.created_at),
    booking_base_amount: totals.base,
    booking_tax_amount: totals.taxes,
    booking_total_amount: totals.total,
    booking_currency: totals.currency,
  }
}

function passengerRows(booking: InventoryBooking, passenger: BookingPassenger): CsvRow[] {
  const details = passenger.pax_details
  const fare = fareForPassenger(booking, details)
  const payments = passenger.payments?.length ? passenger.payments : [null]
  const base = bookingBaseRow(booking)

  return payments.map((payment) => {
    const paymentDetails = payment?.payment_details
    return {
      ...base,
      passenger_prefix: details?.prefix || '',
      passenger_first_name: details?.firstName || '',
      passenger_middle_name: details?.middleName || '',
      passenger_last_name: details?.lastName || '',
      passenger_gender: details?.gender || '',
      passenger_date_of_birth: csvDate(details?.dateOfBirth),
      passenger_type: passengerType(details),
      passenger_status: passenger.pax_booking_status || '',
      airline_record_locator: passenger.airline_record_locator || '',
      ticket_number: passenger.ticket_number || '',
      email: contactValues(details?.contact?.emails, 'email'),
      phone: contactValues(details?.contact?.phones, 'number'),
      fare_pax_code: fare?.passengerDetails?.code || fare?.passengerTypeCode || '',
      fare_pax_type: fare?.passengerDetails?.type || fare?.passengerType || '',
      fare_quantity: String(fare?.passengerDetails?.quantity ?? fare?.quantity ?? ''),
      payment_type: paymentDetails?.type || paymentDetails?.paymentType || '',
      payment_amount: moneyAmount(paymentDetails?.totalAmount),
      payment_currency: moneyCurrency(paymentDetails?.totalAmount, base.booking_currency),
      payment_card: cardLastFour(paymentDetails?.paymentDetails),
    }
  })
}

export function bookingsToCsv(bookings: InventoryBooking[]): string {
  const rows = bookings.flatMap((booking) => {
    const passengers = booking.passengers || []
    if (!passengers.length) return [bookingBaseRow(booking)]
    return passengers.flatMap((passenger) => passengerRows(booking, passenger))
  })

  const header = CSV_COLUMNS.join(',')
  const body = rows.map((row) => CSV_COLUMNS.map((column) => escapeCsv(row[column])).join(','))
  return `\uFEFF${[header, ...body].join('\n')}`
}

export function downloadBookingsCsv(bookings: InventoryBooking[], filename: string) {
  const blob = new Blob([bookingsToCsv(bookings)], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function bookingsCsvFilename(itineraryId?: number | null): string {
  const stamp = csvDate(new Date().toISOString()) || 'export'
  return itineraryId
    ? `bookings-block-${itineraryId}-${stamp}.csv`
    : `bookings-${stamp}.csv`
}
